/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import type { ComponentPropsWithoutRef } from "react";
import ReactMarkdown, { type Components, type ExtraProps } from "react-markdown";
import remarkGfm from "remark-gfm";

type MarkdownContentProps = {
  content: string;
  className?: string;
  headingOffset?: number;
};

/**
 * Clamps a heading level to valid HTML heading range (1-6).
 * @param level - The heading level to clamp
 * @returns A heading level between 1 and 6
 */
const clampHeading = (level: number) => Math.min(6, Math.max(1, level));

/**
 * Builds a React component for a heading with an offset applied.
 * @param level - The base heading level (1-6)
 * @param offset - The offset to apply to the heading level
 * @returns A React component function for the heading
 */
const buildHeadingComponent = (level: number, offset: number) => {
  const Tag = `h${clampHeading(level + offset)}` as
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6";
  return ({
    node: _node,
    ...props
  }: ComponentPropsWithoutRef<"h1"> & ExtraProps) => <Tag {...props} />;
};

/**
 * Checks whether a URL points to an external domain.
 */
const isExternalUrl = (href: string | undefined): boolean =>
  !!href && (href.startsWith('http://') || href.startsWith('https://'));

/**
 * Renders links with target="_blank" for external URLs.
 */
const MarkdownLink = ({
  node: _node,
  href,
  ...props
}: ComponentPropsWithoutRef<"a"> & ExtraProps) => {
  if (isExternalUrl(href)) {
    return <a href={href} target="_blank" rel="noopener noreferrer" {...props} />;
  }
  return <a href={href} {...props} />;
};

/**
 * Renders markdown content with customizable heading levels.
 * @param content - The markdown content to render
 * @param className - Optional CSS class name for the container
 * @param headingOffset - Offset to apply to all heading levels (default: 0)
 * @returns A React component rendering the markdown content
 */
export function MarkdownContent({
  content,
  className,
  headingOffset = 0,
}: MarkdownContentProps) {
  if (!content || !content.trim()) {
    return <div className={className} />;
  }

  const components: Components = {
    h1: buildHeadingComponent(1, headingOffset),
    h2: buildHeadingComponent(2, headingOffset),
    h3: buildHeadingComponent(3, headingOffset),
    h4: buildHeadingComponent(4, headingOffset),
    h5: buildHeadingComponent(5, headingOffset),
    h6: buildHeadingComponent(6, headingOffset),
    a: MarkdownLink,
  };

  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
